import { useCallback, useRef, useState } from 'react';

import type { FormatActionLabel } from '../../../utils/approvalActionLabels';
import { getApprovalActionLabel } from '../../../utils/approvalActionLabels';

import type { ChangedApprovalRule } from './useApprovalRuleEdits';
import { useApprovalRulesMutations } from './useApprovalRulesMutations';

type UseApprovalRulesSubmissionParams = {
  changedRules: readonly ChangedApprovalRule[];
  canSave: boolean;
  formatMessage: FormatActionLabel;
  onSuccess: (hasPendingApproval: boolean) => void;
  onFailure: (error: unknown) => void;
};

function isPendingApprovalResult(result: unknown): boolean {
  return (
    typeof result === 'object' &&
    result !== null &&
    'consensusStatus' in result &&
    result.consensusStatus === 'CONSENSUS_STATUS_REQUIRED'
  );
}

export function useApprovalRulesSubmission({
  changedRules,
  canSave,
  formatMessage,
  onSuccess,
  onFailure,
}: UseApprovalRulesSubmissionParams) {
  const { saveApprovalRule } = useApprovalRulesMutations();
  const isSavingRef = useRef(false);
  const [isSaving, setIsSaving] = useState(false);

  const saveChanges = useCallback(async () => {
    if (isSavingRef.current || !canSave || changedRules.length === 0) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);

    try {
      const settledResults = await Promise.allSettled(
        changedRules.map(({ rule, state, eligibleApproverCount }) =>
          saveApprovalRule({
            rule,
            // TODO(sriram.kumaran): Remove ruleName after the create consensus endpoint no longer requires it.
            ruleName: `${getApprovalActionLabel(rule.action, formatMessage)} approvals`,
            isEnabled: state.isEnabled,
            approvers: state.approvers,
            requirementCount: state.requirementCount,
            eligibleApproverCount,
          }),
        ),
      );
      const failures = settledResults.flatMap((result) =>
        result.status === 'rejected' ? [result.reason] : [],
      );

      if (failures.length > 0) {
        onFailure(
          failures.length === 1
            ? failures[0]
            : new AggregateError(failures, 'Multiple approval rules failed to save.'),
        );
        return;
      }

      const results = settledResults.flatMap((result) =>
        result.status === 'fulfilled' && result.value != null ? [result.value] : [],
      );
      onSuccess(results.some(isPendingApprovalResult));
    } catch (error) {
      onFailure(error);
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [canSave, changedRules, formatMessage, onFailure, onSuccess, saveApprovalRule]);

  return { isSaving, saveChanges };
}
